/**
 * JobMatchSection — Paste a job description and get a match analysis.
 * Uses vacancyService.parseVacancy (with save) + matchVacancy under the hood.
 */

import { useState } from 'react';
import { Button, useToast } from '@/components/common';
import { InlineLoader } from '@shared/ui/loading';
import { vacancyService, type MatchDetails } from '@/services/vacancy.service';
import { ShareScore } from '@/components/common/ShareScore';
import './job-match-section.css';

function scoreColor(score: number): string {
  if (score >= 70) return 'var(--color-success)';
  if (score >= 50) return 'var(--color-warning)';
  return 'var(--color-error)';
}

function scoreFillClass(score: number): string {
  if (score >= 70) return 'job-match__bar-fill--high';
  if (score >= 50) return 'job-match__bar-fill--medium';
  return 'job-match__bar-fill--low';
}

const REC_PREFIX: Record<string, string> = {
  add: 'Add',
  emphasize: 'Emphasize',
  reword: 'Reword',
};

export function JobMatchSection() {
  const toast = useToast();
  const [text, setText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<MatchDetails | null>(null);
  const [parsedTitle, setParsedTitle] = useState<string | null>(null);

  const handleAnalyze = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;

    setIsLoading(true);
    setResult(null);
    try {
      // Step 1: Parse & save vacancy
      const { vacancy, parsed } = await vacancyService.parseVacancy({
        text: trimmed,
        source: 'other',
        saveToLibrary: true,
      });

      const vacancyId = vacancy?.id;
      if (!vacancyId) {
        toast.error('Could not save vacancy. Please sign in to use this feature.');
        return;
      }
      setParsedTitle(parsed?.title || vacancy?.title || null);

      // Step 2: Match against profile
      const matchResult = await vacancyService.matchVacancy(vacancyId);
      setResult(matchResult);
      toast.success(`Match score: ${matchResult.matchScore}%`);
    } catch (error: any) {
      // Skip toast if QuotaWall already handles 429
      if (error?.response?.status === 429) return;
      const msg = error instanceof Error ? error.message : 'Match analysis failed';
      toast.error(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    setText('');
    setResult(null);
    setParsedTitle(null);
  };

  return (
    <div className="job-match">
      <h2 className="job-match__title">Job Match Analysis</h2>
      <p className="job-match__subtitle">
        Paste a job description to see how well your profile matches the role.
      </p>

      {/* Input area */}
      <textarea
        className="job-match__textarea"
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Paste the full job description here..."
        disabled={isLoading}
        rows={6}
      />

      <div className="job-match__actions">
        <Button
          onClick={handleAnalyze}
          disabled={!text.trim() || isLoading}
          size="md"
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <InlineLoader size="sm" /> Analyzing...
            </span>
          ) : result ? (
            'Re-analyze'
          ) : (
            'Analyze Match'
          )}
        </Button>
        {(text || result) && !isLoading && (
          <Button onClick={handleClear} variant="outline" size="sm">
            Clear
          </Button>
        )}
      </div>

      {/* Results */}
      {result && !isLoading && (
        <div className="job-match__results">
          {parsedTitle && (
            <p className="job-match__parsed-title">
              Matched against: <strong>{parsedTitle}</strong>
            </p>
          )}

          {/* Score bar */}
          <div className="job-match__score-section">
            <div className="job-match__score-row">
              <span className="job-match__score-value" style={{ color: scoreColor(result.matchScore) }}>
                {result.matchScore}%
              </span>
              <span className="job-match__score-label">Overall Match</span>
            </div>
            <div className="job-match__bar">
              <div
                className={`job-match__bar-fill ${scoreFillClass(result.matchScore)}`}
                style={{ width: `${result.matchScore}%` }}
              />
            </div>
          </div>

          {/* Skills */}
          <div className="job-match__section">
            <p className="job-match__section-title">
              Skills Match ({result.breakdown.skills.percentage}%)
            </p>
            {result.breakdown.skills.matched.length > 0 && (
              <div className="job-match__chips">
                {result.breakdown.skills.matched.map((s, i) => (
                  <span key={i} className="job-match__chip job-match__chip--matched">{s}</span>
                ))}
              </div>
            )}
            {result.breakdown.skills.missing.length > 0 && (
              <div className="job-match__chips">
                {result.breakdown.skills.missing.map((s, i) => (
                  <span key={i} className="job-match__chip job-match__chip--missing">{s}</span>
                ))}
              </div>
            )}
          </div>

          {/* Experience */}
          <div className="job-match__section">
            <p className="job-match__section-title">
              Experience ({result.breakdown.experience.score}%)
            </p>
            {result.breakdown.experience.matched.length > 0 && (
              <ul className="job-match__list">
                {result.breakdown.experience.matched.map((item, i) => (
                  <li key={i} className="job-match__list-item job-match__list-item--matched">{item}</li>
                ))}
              </ul>
            )}
            {result.breakdown.experience.missing.length > 0 && (
              <ul className="job-match__list">
                {result.breakdown.experience.missing.map((item, i) => (
                  <li key={i} className="job-match__list-item job-match__list-item--missing">{item}</li>
                ))}
              </ul>
            )}
          </div>

          {/* Strengths */}
          {result.strengths.length > 0 && (
            <div className="job-match__section">
              <p className="job-match__section-title">Strengths</p>
              <ul className="job-match__list">
                {result.strengths.map((s, i) => (
                  <li key={i} className="job-match__list-item job-match__list-item--strength">{s}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Recommendations */}
          {result.recommendations.length > 0 && (
            <div className="job-match__section">
              <p className="job-match__section-title">Recommendations</p>
              <ul className="job-match__recommendations">
                {result.recommendations.map((rec, i) => (
                  <li key={i} className="job-match__recommendation">
                    <span className={`job-match__rec-type job-match__rec-type--${rec.type}`}>
                      {REC_PREFIX[rec.type] || rec.type}
                    </span>
                    <span className="job-match__rec-suggestion">{rec.suggestion}</span>
                    <span className="job-match__rec-reason">{rec.reason}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Share match score */}
          <ShareScore score={result.matchScore} />
        </div>
      )}
    </div>
  );
}
